import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useBetSlip, slipId } from '../context/BetSlipContext';

// Owner 2026-10-13: click a horse on Bucket Next Up -> choose Win / Place (back or lay) singles or a parlay leg;
// the choice lands in the bet slip on the right, where the stake is set and the bet placed.
const key = (v: string) => String(v || '').toUpperCase().replace(/^\d+[.]?\s*/, '').replace(/[^A-Z0-9]/g, '');
type BR = { selection_id: number; runner_name: string; best_back?: number | null; best_lay?: number | null; status?: string };
type BM = { market_id: string; market_name?: string; status?: string; runners?: BR[] } | null;
const fmt = (p?: number | null) => (Number(p) > 1 ? '$' + Number(p).toFixed(2) : '—');

const QuickBetPanel: React.FC<{ track: string; race: number; date: string; runner: string; onClose: () => void }> = ({ track, race, date, runner, onClose }) => {
  const { addItem, addMultiple, setOpen, hasItem, multipleItems } = useBetSlip();
  const [win, setWin] = useState<BM>(null);
  const [place, setPlace] = useState<BM>(null);
  const [meeting, setMeeting] = useState(track);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => { setOpen(true); }, [setOpen]);
  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/api/races/${encodeURIComponent(track)}/${race}`, { params: { date }, timeout: 20000 });
        if (!on) return;
        setWin(r.data.betfairMarket || null); setPlace(r.data.betfairPlaceMarket || null);
        if (r.data.race?.meeting_name) setMeeting(r.data.race.meeting_name);
        setErr('');
      } catch (e: any) { if (on) setErr(e?.response?.data?.error || 'Could not load Betfair prices for this race.'); }
      finally { if (on) setLoading(false); }
    };
    void load();
    const t = window.setInterval(load, 15000);
    return () => { on = false; window.clearInterval(t); };
  }, [track, race, date]);

  const wr = win?.runners?.find(x => key(x.runner_name) === key(runner));
  const pr = wr ? place?.runners?.find(x => Number(x.selection_id) === Number(wr.selection_id)) : undefined;
  const num = Number(String(wr?.runner_name || '').match(/^(\d+)/)?.[1]) || undefined;
  const winOpen = !!win && win.status === 'OPEN' && !!wr && wr.status !== 'REMOVED';
  const placeOpen = !!place && place.status === 'OPEN' && !!pr && pr.status !== 'REMOVED';

  const single = (type: 'WIN' | 'PLACE', side: 'BACK' | 'LAY') => {
    const m = type === 'WIN' ? win : place, r = type === 'WIN' ? wr : pr, ok = type === 'WIN' ? winOpen : placeOpen;
    const price = Number(side === 'BACK' ? r?.best_back : r?.best_lay);
    const inSlip = !!m && !!r && hasItem(slipId(m.market_id, Number(r.selection_id), side));
    return <button type="button" key={type + side} disabled={!ok || !(price > 1)}
      className={`bf-btn ${side === 'BACK' ? 'bf-back' : 'bf-lay'}${inSlip ? ' in-slip' : ''}`}
      title={`${side === 'BACK' ? 'Back' : 'Lay'} ${runner} ${type}${inSlip ? ' (in slip, click to remove)' : ''}`}
      onClick={() => {
        if (!m || !r || !win || !(price > 1)) return;
        addItem({ marketId: m.market_id, winMarketId: win.market_id, marketType: type, number: num, stake: 0,
          selectionId: Number(r.selection_id), runnerName: runner, side, price,
          marketName: type === 'PLACE' ? 'PLACE' : (win.market_name || 'WIN'), meetingName: meeting, raceNumber: race, date });
      }}>
      <span className="bf-price">{side === 'BACK' ? 'Back ' : 'Lay '}{fmt(price)}</span>
    </button>;
  };
  const leg = (type: 'WIN' | 'PLACE' | 'EW') => {
    const on = !!wr && !!win && multipleItems.some(x => x.win_market_id === win.market_id && Number(x.selection_id) === Number(wr.selection_id) && x.type === type);
    return <button type="button" key={type} aria-pressed={on} className={on ? 'quickbet-leg on' : 'quickbet-leg'}
      disabled={!winOpen || (type !== 'WIN' && !placeOpen)}
      title={`Add ${runner} to a parlay as ${type === 'EW' ? 'each way' : type.toLowerCase()}`}
      onClick={() => wr && win && addMultiple({ win_market_id: win.market_id, selection_id: Number(wr.selection_id), runner, number: num,
        track: meeting, race, type })}>
      {type === 'WIN' ? 'Win leg' : type === 'PLACE' ? 'Place leg' : 'Each-way leg'}
    </button>;
  };

  return <div className="quickbet" role="group" aria-label={`Bet on ${runner}`}>
    <div className="quickbet-head">
      <strong>{num ? num + '. ' : ''}{runner}</strong>
      <span className="quickbet-sub">{meeting} R{race}</span>
      <button type="button" className="quickbet-close" aria-label="Close" onClick={onClose}>×</button>
    </div>
    {loading && <p role="status">Loading Betfair prices…</p>}
    {err && <p className="sr-error" role="alert">{err}</p>}
    {!loading && !err && !wr && <p>This horse isn't in the Betfair win market for this race.</p>}
    {!loading && wr && !winOpen && <p className="rc-note">Betting is closed for this race ({wr.status === 'REMOVED' ? 'scratched' : (win?.status || 'no market').toLowerCase()}).</p>}
    {wr && <>
      <div className="quickbet-row"><span className="quickbet-label">Win</span>{single('WIN', 'BACK')}{single('WIN', 'LAY')}</div>
      <div className="quickbet-row"><span className="quickbet-label">Place</span>
        {place ? <>{single('PLACE', 'BACK')}{single('PLACE', 'LAY')}</> : <em>No place market</em>}</div>
      <div className="quickbet-row"><span className="quickbet-label">Parlay</span>{leg('WIN')}{leg('PLACE')}{leg('EW')}</div>
      <p className="rc-note">Your choice goes to the bet slip on the right — set the stake there. Prices refresh every 15 seconds.</p>
    </>}
  </div>;
};
export default QuickBetPanel;
